import type {LabelPickerLabel$data, LabelPickerLabel$key} from '@github-ui/item-picker/LabelPickerLabel.graphql'
import {
  LabelFragment,
  LabelPickerBase,
  LazyLabelPicker,
  LazyLabelPickerOnClick,
} from '@github-ui/item-picker/LabelPicker'
import {useMemo, Suspense, type HTMLAttributes} from 'react'
import {readInlineData} from 'react-relay'
import {graphql, useFragment, usePaginationFragment, type PreloadedQuery} from 'react-relay/hooks'

import {LABELS} from '../../constants/labels'
import {TEST_IDS} from '../../constants/test-ids'
import {LabelsList as Labels, type Label} from '@github-ui/labels-list'
import {ReadonlySectionHeader} from '@github-ui/issue-metadata/ReadonlySectionHeader'
import {SectionHeader} from '@github-ui/issue-metadata/SectionHeader'
import {Section} from '@github-ui/issue-metadata/Section'
import type {LabelsSectionAssignedLabelsQuery} from './__generated__/LabelsSectionAssignedLabelsQuery.graphql'
import LabelsSectionAssignedLabelsFragment, {
  type LabelsSectionAssignedLabels$key,
} from './__generated__/LabelsSectionAssignedLabels.graphql'
import type {LabelsSectionFragment$key} from './__generated__/LabelsSectionFragment.graphql'
import type {SxProp} from '@primer/react'
import type {LabelPickerQuery} from '@github-ui/item-picker/LabelPickerQuery.graphql'

const ReadonlyLabelsSectionHeader = () => <ReadonlySectionHeader title={LABELS.sectionTitles.labels} />
const LabelsSectionHeader = (props: HTMLAttributes<HTMLElement>) => (
  <SectionHeader title={LABELS.sectionTitles.labels} buttonProps={props} />
)

type LabelsSectionProps = {
  sectionHeader: JSX.Element
  labels: Label[]
} & SxProp
const LabelsSection = ({sectionHeader, labels}: LabelsSectionProps) => {
  return (
    <Section sectionHeader={sectionHeader} emptyText={labels.length > 0 ? undefined : LABELS.emptySections.labels}>
      <Labels labels={labels} testId={TEST_IDS.issueLabels} />
    </Section>
  )
}

type CreateIssueLabelsSectionProps = {
  readonly: boolean
  labels: LabelPickerLabel$data[]
  labelQueryRef: PreloadedQuery<LabelPickerQuery, Record<string, unknown>>
  onSelectionChange: (labels: LabelPickerLabel$data[]) => void
  shortcutEnabled: boolean
}
export const CreateIssueLabelsSection = ({
  readonly,
  labels,
  labelQueryRef,
  onSelectionChange,
  ...sharedConfigProps
}: CreateIssueLabelsSectionProps) => {
  const sectionHeader = readonly ? (
    <ReadonlyLabelsSectionHeader />
  ) : (
    <LabelPickerBase
      readonly={readonly}
      labels={labels}
      labelQueryRef={labelQueryRef}
      onSelectionChange={onSelectionChange}
      anchorElement={LabelsSectionHeader}
      {...sharedConfigProps}
    />
  )
  return <LabelsSection sectionHeader={sectionHeader} labels={labels} />
}

graphql`
  fragment LabelsSectionAssignedLabels on Labelable
  @argumentDefinitions(cursor: {type: "String"}, assignedLabelPageSize: {type: "Int", defaultValue: 20})
  @refetchable(queryName: "LabelsSectionAssignedLabelsQuery") {
    labels(first: $assignedLabelPageSize, after: $cursor) @connection(key: "MetadataSectionAssignedLabels_labels") {
      edges {
        node {
          # eslint-disable-next-line relay/must-colocate-fragment-spreads
          ...LabelPickerLabel
        }
      }
    }
  }
`

const labelsSectionFragment = graphql`
  fragment LabelsSectionFragment on Issue {
    id
    repository {
      name
      owner {
        login
      }
      isArchived
    }
    ...LabelsSectionAssignedLabels
    viewerCanLabel
  }
`

export type EditIssueLabelsSectionProps = {
  onIssueUpdate?: () => void
  singleKeyShortcutsEnabled: boolean
  prefetchInteractionDataEnabled: boolean
  issue: LabelsSectionFragment$key
}

export function EditIssueLabelsSection({
  issue,
  onIssueUpdate,
  singleKeyShortcutsEnabled,
  prefetchInteractionDataEnabled,
}: EditIssueLabelsSectionProps) {
  const data = useFragment(labelsSectionFragment, issue)
  const {
    repository: {
      owner: {login: owner},
      name: repo,
      isArchived: isRepositoryArchived,
    },
  } = data
  const {data: labelDataRaw} = usePaginationFragment<LabelsSectionAssignedLabelsQuery, LabelsSectionAssignedLabels$key>(
    LabelsSectionAssignedLabelsFragment,
    data,
  )

  const labelData = labelDataRaw

  const {id: issueId, viewerCanLabel} = data

  const labels = useMemo(
    () =>
      labelData.labels?.edges?.flatMap(e =>
        // eslint-disable-next-line no-restricted-syntax
        e?.node ? [readInlineData<LabelPickerLabel$key>(LabelFragment, e?.node)] : [],
      ) || [],
    [labelData],
  )

  const readonlyLabel = !viewerCanLabel || isRepositoryArchived

  const labelSectionHeader = useMemo(() => {
    if (readonlyLabel) {
      return <ReadonlyLabelsSectionHeader />
    }

    return prefetchInteractionDataEnabled ? (
      <Suspense fallback={<ReadonlyLabelsSectionHeader />}>
        <LazyLabelPicker
          labelableId={issueId}
          repo={repo}
          owner={owner}
          labelableTypeName={'Issue'}
          readonly={readonlyLabel}
          shortcutEnabled={singleKeyShortcutsEnabled}
          labels={labels}
          onIssueUpdate={onIssueUpdate}
          anchorElement={LabelsSectionHeader}
        />
      </Suspense>
    ) : (
      <LazyLabelPickerOnClick
        labelableId={issueId}
        repo={repo}
        owner={owner}
        labelableTypeName={'Issue'}
        readonly={readonlyLabel}
        shortcutEnabled={singleKeyShortcutsEnabled}
        labels={labels}
        onIssueUpdate={onIssueUpdate}
        anchorElement={LabelsSectionHeader}
      />
    )
  }, [
    readonlyLabel,
    prefetchInteractionDataEnabled,
    issueId,
    repo,
    owner,
    singleKeyShortcutsEnabled,
    labels,
    onIssueUpdate,
  ])

  return <LabelsSection sectionHeader={labelSectionHeader} labels={labels} />
}

try{ ReadonlyLabelsSectionHeader.displayName ||= 'ReadonlyLabelsSectionHeader' } catch {}
try{ LabelsSectionHeader.displayName ||= 'LabelsSectionHeader' } catch {}
try{ LabelsSection.displayName ||= 'LabelsSection' } catch {}
try{ CreateIssueLabelsSection.displayName ||= 'CreateIssueLabelsSection' } catch {}
try{ EditIssueLabelsSection.displayName ||= 'EditIssueLabelsSection' } catch {}